/* Luma Image Compare: before/after slider custom element
   Usage:
     <luma-image-compare aspect="4/3" start="50">
       <img slot="before" src="..." alt="Before">
       <img slot="after"  src="..." alt="After">
     </luma-image-compare>

   Accessibility: backed by a native <input type="range"> so arrow keys,
   Home/End, Page Up/Down, screen readers, and touch/mouse all work for free.
*/
(function () {
  if (window.customElements && customElements.get('luma-image-compare')) return;

  const STYLE = `
    :host {
      display: block;
      position: relative;
      max-width: 100%;
      aspect-ratio: var(--luma-ic-aspect, 4 / 3);
      overflow: hidden;
      border-radius: var(--luma-ic-radius, 12px);
      background: #000;
      --luma-ic-clip: 50%;
      isolation: isolate;
      margin-left: auto;
      margin-right: auto;
    }
    .luma-ic__frame {
      position: absolute;
      inset: 0;
      overflow: hidden;
    }
    ::slotted(img) {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      user-select: none;
      -webkit-user-drag: none;
    }
    /* Horizontal (default): clip on the right of the before image */
    :host(:not([orientation="vertical"])) ::slotted(img[slot="before"]) {
      z-index: 2;
      clip-path: inset(0 calc(100% - var(--luma-ic-clip)) 0 0);
    }
    /* Vertical: clip on the bottom of the before image (slide up/down) */
    :host([orientation="vertical"]) ::slotted(img[slot="before"]) {
      z-index: 2;
      clip-path: inset(0 0 calc(100% - var(--luma-ic-clip)) 0);
    }
    ::slotted(img[slot="after"]) {
      z-index: 1;
    }

    /* Divider line */
    .luma-ic__divider {
      position: absolute;
      background: #ffffff;
      box-shadow: 0 0 6px rgba(0, 0, 0, 0.45);
      pointer-events: none;
      z-index: 3;
    }
    :host(:not([orientation="vertical"])) .luma-ic__divider {
      top: 0;
      bottom: 0;
      left: var(--luma-ic-clip);
      width: 2px;
      transform: translateX(-50%);
    }
    :host([orientation="vertical"]) .luma-ic__divider {
      left: 0;
      right: 0;
      top: var(--luma-ic-clip);
      height: 2px;
      transform: translateY(-50%);
    }

    /* Handle */
    .luma-ic__handle {
      position: absolute;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #ffffff;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
      display: grid;
      place-items: center;
      pointer-events: none;
      z-index: 4;
      color: #111;
    }
    :host(:not([orientation="vertical"])) .luma-ic__handle {
      top: 50%;
      left: var(--luma-ic-clip);
      transform: translate(-50%, -50%);
    }
    :host([orientation="vertical"]) .luma-ic__handle {
      left: 50%;
      top: var(--luma-ic-clip);
      transform: translate(-50%, -50%);
    }
    :host([orientation="vertical"]) .luma-ic__handle svg {
      transform: rotate(90deg);
    }
    .luma-ic__handle svg {
      width: 22px;
      height: 22px;
      fill: currentColor;
    }

    /* Labels */
    .luma-ic__label {
      position: absolute;
      background: rgba(0, 0, 0, 0.62);
      color: #ffffff;
      font: 600 13px/1 inherit;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      padding: 6px 10px;
      border-radius: 4px;
      pointer-events: none;
      z-index: 5;
    }
    :host(:not([orientation="vertical"])) .luma-ic__label {
      top: 50%;
      transform: translateY(-50%);
    }
    :host(:not([orientation="vertical"])) .luma-ic__label--before { left: 12px; }
    :host(:not([orientation="vertical"])) .luma-ic__label--after  { right: 12px; }
    :host([orientation="vertical"]) .luma-ic__label {
      left: 50%;
      transform: translateX(-50%);
    }
    :host([orientation="vertical"]) .luma-ic__label--before { top: 12px; }
    :host([orientation="vertical"]) .luma-ic__label--after  { bottom: 12px; }

    @media (max-width: 559px) {
      .luma-ic__label {
        font-size: 10px;
        padding: 4px 7px;
        letter-spacing: 0.03em;
      }
      :host(:not([orientation="vertical"])) .luma-ic__label--before { left: 8px; }
      :host(:not([orientation="vertical"])) .luma-ic__label--after  { right: 8px; }
      :host([orientation="vertical"]) .luma-ic__label--before { top: 8px; }
      :host([orientation="vertical"]) .luma-ic__label--after  { bottom: 8px; }
      .luma-ic__handle {
        width: 36px;
        height: 36px;
      }
      .luma-ic__handle svg {
        width: 18px;
        height: 18px;
      }
    }

    /* Range input (invisible, positioned to capture drag) */
    .luma-ic__range {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      background: transparent;
      opacity: 0;
      z-index: 10;
    }
    :host(:not([orientation="vertical"])) .luma-ic__range { cursor: ew-resize; }
    :host([orientation="vertical"]) .luma-ic__range { cursor: ns-resize; }
    .luma-ic__range:focus-visible + .luma-ic__divider,
    .luma-ic__range:focus-visible ~ .luma-ic__handle {
      outline: 3px solid #B8962E;
      outline-offset: 2px;
    }
  `;

  const SVG_NS = 'http://www.w3.org/2000/svg';

  function makeHandleIcon() {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M8.5 5L3 12l5.5 7v-4h7v4l5.5-7-5.5-7v4h-7V5z');
    svg.appendChild(path);
    return svg;
  }

  class LumaImageCompare extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
      return ['aspect', 'start', 'before-label', 'after-label', 'orientation'];
    }

    connectedCallback() {
      this.build();
      this.applyAspect();
      this.setClip(this.startValue);
    }

    attributeChangedCallback() {
      if (!this.shadowRoot.firstChild) return;
      this.applyAspect();
      this.updateLabels();
      this.range.value = String(this.startValue);
      this.setClip(this.startValue);
    }

    get startValue() {
      const raw = parseFloat(this.getAttribute('start'));
      if (Number.isFinite(raw) && raw >= 0 && raw <= 100) return raw;
      return 50;
    }

    build() {
      const root = this.shadowRoot;
      while (root.firstChild) root.removeChild(root.firstChild);

      const styleEl = document.createElement('style');
      styleEl.textContent = STYLE;
      root.appendChild(styleEl);

      const frame = document.createElement('div');
      frame.className = 'luma-ic__frame';
      frame.setAttribute('part', 'frame');

      const beforeSlot = document.createElement('slot');
      beforeSlot.setAttribute('name', 'before');
      const afterSlot = document.createElement('slot');
      afterSlot.setAttribute('name', 'after');

      const beforeLabel = document.createElement('span');
      beforeLabel.className = 'luma-ic__label luma-ic__label--before';
      beforeLabel.textContent = this.getAttribute('before-label') || 'Before';

      const afterLabel = document.createElement('span');
      afterLabel.className = 'luma-ic__label luma-ic__label--after';
      afterLabel.textContent = this.getAttribute('after-label') || 'After';

      const range = document.createElement('input');
      range.className = 'luma-ic__range';
      range.type = 'range';
      range.min = '0';
      range.max = '100';
      range.step = '0.1';
      range.value = String(this.startValue);
      range.setAttribute('aria-label', 'Reveal before and after images');

      const divider = document.createElement('div');
      divider.className = 'luma-ic__divider';
      divider.setAttribute('aria-hidden', 'true');

      const handle = document.createElement('div');
      handle.className = 'luma-ic__handle';
      handle.setAttribute('aria-hidden', 'true');
      handle.appendChild(makeHandleIcon());

      frame.appendChild(beforeSlot);
      frame.appendChild(afterSlot);
      frame.appendChild(beforeLabel);
      frame.appendChild(afterLabel);
      frame.appendChild(range);
      frame.appendChild(divider);
      frame.appendChild(handle);
      root.appendChild(frame);

      this.frame = frame;
      this.range = range;
      this._beforeLabelEl = beforeLabel;
      this._afterLabelEl = afterLabel;

      // Native range input: handles horizontal mode end-to-end (mouse + keyboard).
      // In vertical mode, the native range tracks X-axis only, which doesn't match
      // the vertical UX, so we skip its updates and let the custom pointer handler
      // be the single source of truth.
      range.addEventListener('input', () => {
        if (this.getAttribute('orientation') === 'vertical') return;
        this.setClip(parseFloat(range.value));
      });

      // Pointer drag handler: tracks X for horizontal, Y for vertical.
      const onPointerMove = (e) => {
        const rect = frame.getBoundingClientRect();
        let pct;
        if (this.getAttribute('orientation') === 'vertical') {
          pct = ((e.clientY - rect.top) / rect.height) * 100;
        } else {
          pct = ((e.clientX - rect.left) / rect.width) * 100;
        }
        pct = Math.max(0, Math.min(100, pct));
        range.value = String(pct);
        this.setClip(pct);
      };
      const stop = () => {
        window.removeEventListener('pointermove', onPointerMove);
      };
      frame.addEventListener('pointerdown', (e) => {
        // In vertical mode, prevent the native range input from processing the
        // pointer event (which would clobber the clip with an X-based value).
        if (this.getAttribute('orientation') === 'vertical') {
          e.preventDefault();
        }
        onPointerMove(e);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', stop, { once: true });
      });

      // Keyboard: arrow keys on the range input. In vertical mode we intercept
      // all arrows so the native range can't bump the value.
      range.addEventListener('keydown', (e) => {
        const isVertical = this.getAttribute('orientation') === 'vertical';
        const cur = parseFloat(range.value) || 0;
        let next = cur;
        const step = e.shiftKey ? 10 : 2;
        if (isVertical) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { next = cur + step; e.preventDefault(); }
          if (e.key === 'ArrowUp'   || e.key === 'ArrowLeft')  { next = cur - step; e.preventDefault(); }
        }
        if (e.key === 'Home') { next = 0;   e.preventDefault(); }
        if (e.key === 'End')  { next = 100; e.preventDefault(); }
        if (next !== cur) {
          next = Math.max(0, Math.min(100, next));
          range.value = String(next);
          this.setClip(next);
        }
      });
    }

    applyAspect() {
      const aspect = this.getAttribute('aspect') || '4/3';
      const normalized = aspect.replace(':', '/').trim();
      this.style.setProperty('--luma-ic-aspect', normalized);
    }

    updateLabels() {
      if (this._beforeLabelEl) {
        this._beforeLabelEl.textContent = this.getAttribute('before-label') || 'Before';
      }
      if (this._afterLabelEl) {
        this._afterLabelEl.textContent = this.getAttribute('after-label') || 'After';
      }
    }

    setClip(value) {
      const v = Math.max(0, Math.min(100, Number(value) || 0));
      this.style.setProperty('--luma-ic-clip', v + '%');
    }
  }

  customElements.define('luma-image-compare', LumaImageCompare);
})();
